import type { TimeComponent } from "./types";

/** Legacy alias kept for `_customTime.ts`. */
export type SettingsZ = {
	Z?: boolean;
};

/**
 * Settings for factories and setter methods. `Z: true` runs this one set
 * in UTC without changing the object; `theUTCTime` is the persistent variant.
 * For IANA zones, see `ReadOptions.timeZone` (render-side only).
 */
export type Settings = {
	Z?: boolean;
	/** Add instead of set. `YYYY`/`MM`/`DD` add calendar units (wall clock kept); `HH`/`mm`/`ss`/`sss` add elapsed time. */
	add?: boolean;
	overflow?: boolean;
	// An explicit `undefined` is allowed — absent means "leave it", so a caller forwarding a `string | undefined` need not guard
	epoch?: number | undefined;
	YYYY?: string | number | undefined;
	MM?: string | number | undefined;
	DD?: string | number | undefined;
	/** ISO time portion like `"14:30:00.123"`, leading `T` optional, no zone; `0` resets to midnight. */
	T?: string | 0 | undefined;
	HH?: string | number | undefined;
	mm?: string | number | undefined;
	ss?: string | number | undefined;
	sss?: string | number | undefined;
};

/**
 * One-off render options for reader methods. Override the TimeDate's
 * factory-set `_Z` for the call; TimeDate itself is unchanged.
 */
export type ReadOptions = {
	/** UTC flag, native-getter path. For IANA zones, use `timeZone`. */
	Z?: boolean;
	/** IANA zone (e.g. `"Europe/Oslo"`). Intl path. Wins over `Z`. */
	timeZone?: string;
};

/**
 * Is the value one a parameter position takes? The kind only — `{ YYYY: "abc" }` is an
 * accepted setting whose year the gate then refuses. A value outside the position cannot
 * be honoured: `theTime(getDate)` with the call forgotten is a function, not an init, so it
 * gives an `Invalid Date`. Construction never throws, and `orThrow()` is how a caller asks it to.
 */
export const isAccepted = {
	setting: (v: unknown): v is Settings =>
		typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date),
	component: (v: unknown) =>
		typeof v === "string" || isAccepted.setting(v),
};

/** The keys the setters honour — everything else in `Settings` is a flag. */
export const settableComponents = new Set(["epoch", "YYYY", "MM", "DD", "T", "HH", "mm", "ss", "sss"]);

// `T` takes the time portion of a date time string, the leading `T` optional. A zone
// cannot be honoured — `Settings` has no offset — so one is rejected, not stripped.
const timeString = /^T?\d{2}(?::\d{2}(?::\d{2}(?:\.\d+)?)?)?$/;

/**
 * Is `opt[k]` a value the setters can read? `T` is a time string, the rest are numbers.
 * `MM: 13` is readable and rolls, as `Date` does; `MM: "abc"` is not a value at all.
 * `+""` and `+" "` are 0, so a blank needs its own check — it is not a zero.
 */
const isReadable = (opt: Settings, k: string) =>
	((v) =>
		v === undefined ? true
			: k === "T" ? v === 0 || (typeof v === "string" && timeString.test(v))
				: typeof v === "number" ? Number.isFinite(v)
					: typeof v === "string" && /\S/.test(v) && Number.isFinite(+v)
	)(opt[k as keyof Settings]);

/**
 * Sets and returns a new date from `d` Date object.
 * @param opt: Settings - props for how to set Date
 * @param d: Date - pass a Date object, if not, `new Date()` (default)
 * @param returnNew: boolean - `true` returns a new Date object (default), or pass `false` to mutate `d` in place. Mutation is safe only when the caller owns `d`.
 * @returns Date - an Invalid Date `d` is returned as-is, with no settings applied
 */
export const setNewDate = (
	opt: Settings = {},
	d?: Date,
	returnNew = true,
): Date => {
	const D = !d ? new Date() : returnNew && d ? new Date(d) : d;
	// Return Date unchanged if invalid or no settings
	if (Number.isNaN(D.getTime()) || Object.keys(opt).length === 0) return D;
	// One gate for every setter path: a value that cannot be read invalidates
	for (const k in opt)
		if (settableComponents.has(k) && !isReadable(opt, k)) return void D.setTime(NaN), D;

	const setMonthFallbackClamp = (i: number) => {
		// If overflow, set month directly (lets native Date overflow days into next month)
		if (opt?.overflow) return void (() => (opt.Z ? D.setUTCMonth(i) : D.setMonth(i)))();
		// Clamp day to last day of target month
		const day = opt.Z ? D.getUTCDate() : D.getDate();
		(() => (opt.Z ? D.setUTCDate(1) : D.setDate(1)))(); // day to 1 to avoid month overflow
		(() => (opt.Z ? D.setUTCMonth(i) : D.setMonth(i)))();
		const end = new Date(opt.Z ? D.getUTCFullYear() : D.getFullYear(), i + 1, 0).getDate();
		(() => opt.Z ? D.setUTCDate(Math.min(day, end)) : D.setDate(Math.min(day, end)))();
	};

	// Set by `epoch`
	if (typeof opt.epoch === "number" && !opt.add) D.setTime(opt.epoch);
	else if (typeof opt.epoch === "number") D.setTime(D.getTime() + opt.epoch);
	// Set by `YYYY`, `MM`, `DD`
	if (typeof opt.YYYY !== "undefined") {
		const y = opt.add
			? (opt.Z ? D.getUTCFullYear() : D.getFullYear()) + +opt.YYYY
			: +opt.YYYY;
		const m = opt.Z ? D.getUTCMonth() : D.getMonth();
		(() => (opt.Z ? D.setUTCFullYear(y) : D.setFullYear(y)))();
		// Clamp: if setting year caused month drift (e.g. leap day Feb 29 -> Mar 01),
		// day 0 rolls back to last day of the intended month (e.g. Feb 28)
		if ((opt.Z ? D.getUTCMonth() : D.getMonth()) !== m && !opt.overflow)
			(() => (opt.Z ? D.setUTCDate(0) : D.setDate(0)))();
	}
	if (typeof opt.MM !== "undefined")
		setMonthFallbackClamp(
			opt.add
				? (opt.Z ? D.getUTCMonth() : D.getMonth()) + +opt.MM
				: +opt.MM - 1,
		);
	if (typeof opt.DD !== "undefined") {
		const d = opt.add
			? (opt.Z ? D.getUTCDate() : D.getDate()) + +opt.DD
			: +opt.DD;
		(() => (opt.Z ? D.setUTCDate(d) : D.setDate(d)))();
	}
	// Set by `T`, ISO time string — the gate above has it matching `timeString`
	if (typeof opt.T === "string" || opt.T === 0) {
		const [hh, mm, sssss] = `${opt.T}`.replace(/^T/, "").split(":");
		const [ss, frac] = (sssss || "").split(".");
		// A fraction is a decimal, not a count — `.1` is 100ms, and digits past three are dropped
		const sss = frac === undefined ? undefined : frac.padEnd(3, "0").slice(0, 3);
		// Set hours, minute, second, millisecond — a part left out zeroes in the same mode
		const n = (part?: string) => (part ? +part : 0);
		(() => (opt.Z ? D.setUTCHours(n(hh)) : D.setHours(n(hh))))();
		(() => (opt.Z ? D.setUTCMinutes(n(mm)) : D.setMinutes(n(mm))))();
		(() => (opt.Z ? D.setUTCSeconds(n(ss)) : D.setSeconds(n(ss))))();
		(() => (opt.Z ? D.setUTCMilliseconds(n(sss)) : D.setMilliseconds(n(sss))))();
	}
	// Set hours, minutes, seconds, milliseconds
	if (typeof opt.HH !== "undefined") {
		const h = opt.add ? D.getUTCHours() + +opt.HH : +opt.HH;
		(() => (opt.Z || opt.add ? D.setUTCHours(h) : D.setHours(h)))();
	}
	if (typeof opt.mm !== "undefined") {
		const m = opt.add ? D.getUTCMinutes() + +opt.mm : +opt.mm;
		(() => (opt.Z || opt.add ? D.setUTCMinutes(m) : D.setMinutes(m)))();
	}
	if (typeof opt.ss !== "undefined") {
		const s = opt.add ? D.getUTCSeconds() + +opt.ss : +opt.ss;
		(() => (opt.Z || opt.add ? D.setUTCSeconds(s) : D.setSeconds(s)))();
	}
	if (typeof opt.sss !== "undefined") {
		const ms = opt.add ? D.getUTCMilliseconds() + +opt.sss : +opt.sss;
		(() => opt.Z || opt.add ? D.setUTCMilliseconds(ms) : D.setMilliseconds(ms))();
	}

	return D;
};

// Lifetime-scoped formatter cache (Intl constructor is the expensive part).
const fmtCache = new Map<string, Intl.DateTimeFormat>();
const getFormatter = (
	lang: string,
	o: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
	const key = `${lang}|${o.timeZone}|${o.year}|${o.month}|${o.day}|${o.hour}|${o.minute}|${o.second}|${o.fractionalSecondDigits}`;
	return (
		fmtCache.get(key) ??
		(() => {
			const f = new Intl.DateTimeFormat(lang, { hour12: false, ...o });
			fmtCache.set(key, f);
			return f;
		})()
	);
};

/** Intl-formatter read of one component from `date` in the given `timeZone` (IANA zone string, e.g. `"Europe/Oslo"`). ISO name → padded string, Temporal name → number, `"epoch"`/no name → ms number, a name that is not a component → `""`. */
export const dateTimeGet = (
	init?:
		| TimeComponent
		| { component: TimeComponent; timeZone?: string }
		// | Intl.DateTimeFormatOptions // would require getting the keys and recursive for more components
		,
	date?: Date,
): string | number => {
	const d = date || new Date();
	const { component: requested, timeZone } = typeof init === "object"
			? init // when object, use as options
			: {
					// when string, use as component, else will be epoch
					component: typeof init === "string" ? init : "",
					// defaults to host-local
					timeZone: undefined as string | undefined,
				};
	const tzOpt = timeZone ? { timeZone } : {};
	const getInTz = (values: Intl.DateTimeFormatOptions, d: Date) =>
		getFormatter("en-US", { ...tzOpt, ...values }).format(d);
	const getPartInTz = (values: Intl.DateTimeFormatOptions, type: string) =>
		getFormatter("en-US", { ...tzOpt, ...values }).formatToParts(d).find((p) => p.type === type)?.value ?? "";
	// cast to number if Temporal style, passthrough as string if ISO style
	const numIf = (temporal: string) => (s: string) =>
		requested === temporal ? +s : s;

	// `year` uses `formatToParts` so era markers don't leak in
	if (/^(year|YYYY)$/.test(requested)) return numIf("year")(getPartInTz({ year: "numeric" }, "year").padStart(4, "0"));
	if (/^(month|MM)$/.test(requested)) return numIf("month")(getInTz({ month: "2-digit" }, d));
	if (/^(day|DD)$/.test(requested)) return numIf("day")(getInTz({ day: "2-digit" }, d));
	if (/^(hour|HH)$/.test(requested)) return numIf("hour")(getInTz({ hour: "2-digit" }, d));
	if (/^(minute|mm)$/.test(requested)) return numIf("minute")(getInTz({ minute: "2-digit" }, d));
	if (/^(second|ss)$/.test(requested)) return numIf("second")(getInTz({ second: "2-digit" }, d));
	if (/^(millisecond|sss)$/.test(requested)) return numIf("millisecond")(getInTz({ fractionalSecondDigits: 3 }, d));
	// An unknown name is not an absent name — only these two mean the timestamp
	return requested === "" || requested === "epoch" ? d.getTime() : "";
};

/** Native-getter read of one component from `D`. ISO name → padded string, Temporal name → number, `"epoch"`/no name → ms number, a name that is not a component → `""`. `UTC: true` uses `getUTC*` methods. */
export const dateGet = (
	init?: TimeComponent | { UTC?: boolean; component?: TimeComponent },
	D = new Date(),
): string | number => {
	const { component: requested = "", UTC: Z } =
		typeof init === "object" ? init : { component: init, UTC: false };
	// pad if ISO style, passthrough as number if Temporal style. A non-finite value has
	// no padded form, so the empty string — falsy, and renders as the absence it is.
	const padIf = (iso: string) => (n: number) =>
		requested !== iso ? n : Number.isFinite(n) ? String(n).padStart(iso.length, "0") : "";
	// Not a `get(suffix)` helper: a computed method name cannot be inlined, and costs more than the read
	// Return value (month is `+1` since `getMonth()` is 0-indexed)
	if (/^(year|YYYY)$/.test(requested)) return padIf("YYYY")(Z ? D.getUTCFullYear() : D.getFullYear());
	if (/^(month|MM)$/.test(requested)) return padIf("MM")((Z ? D.getUTCMonth() : D.getMonth()) + 1);
	if (/^(day|DD)$/.test(requested)) return padIf("DD")(Z ? D.getUTCDate() : D.getDate());
	if (/^(hour|HH)$/.test(requested)) return padIf("HH")(Z ? D.getUTCHours() : D.getHours());
	if (/^(minute|mm)$/.test(requested)) return padIf("mm")(Z ? D.getUTCMinutes() : D.getMinutes());
	if (/^(second|ss)$/.test(requested)) return padIf("ss")(Z ? D.getUTCSeconds() : D.getSeconds());
	if (/^(millisecond|sss)$/.test(requested)) return padIf("sss")(Z ? D.getUTCMilliseconds() : D.getMilliseconds());
	// An unknown name is not an absent name — only these two mean the timestamp
	return requested === "" || requested === "epoch" ? D.getTime() : "";
};

